import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

export type OnboardingTemplateTermsView = {
  vendorDisplayName: string;
  name: string;
  description: string;
  effectiveDatePolicy: string;
  endDatePolicy: string;
  feeSchedule: ReadonlyArray<{
    name: string;
    currency: string;
    fixedAmount: number;
  }>;
};

const ServiceTerms = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
}));

const TermDetails = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  margin: theme.spacing(2, 0),
}));

const TermDetail = styled(Box)(() => ({
  minWidth: 0,
}));

const OnboardingFeeAmountFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

export default function OnboardingTemplateTerms(props: Readonly<{
  terms: OnboardingTemplateTermsView;
  showProposalNotice?: boolean;
}>) {
  const { terms } = props;
  const showProposalNotice = props.showProposalNotice ?? true;

  return (
    <ServiceTerms data-testid="onboarding-template-terms">
      <Typography component="h4" variant="h6">
        {terms.name}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {terms.description}
      </Typography>
      <TermDetails>
        <TermDetail>
          <Typography color="text.secondary" variant="caption">
            Service provider
          </Typography>
          <Typography>{terms.vendorDisplayName}</Typography>
        </TermDetail>
        <TermDetail>
          <Typography color="text.secondary" variant="caption">
            Fee schedule
          </Typography>
          {terms.feeSchedule.map((fee, index) => (
            <Typography key={`${fee.name}-${fee.currency}-${fee.fixedAmount}-${index}`}>
              {fee.name}: {formatOnboardingFee(fee.fixedAmount, fee.currency)}
            </Typography>
          ))}
        </TermDetail>
        <TermDetail>
          <Typography color="text.secondary" variant="caption">
            Start date
          </Typography>
          <Typography>{formatEffectiveDatePolicy(terms.effectiveDatePolicy)}</Typography>
        </TermDetail>
        <TermDetail>
          <Typography color="text.secondary" variant="caption">
            End date
          </Typography>
          <Typography>{formatEndDatePolicy(terms.endDatePolicy)}</Typography>
        </TermDetail>
      </TermDetails>
      {showProposalNotice && (
        <Alert severity="info">
          AttorneyHub will submit the first proposal when you activate the organization. You will review,
          approve, and hire the agreement in the proposal workspace.
        </Alert>
      )}
    </ServiceTerms>
  );
}

function formatOnboardingFee(amount: number, currency: string): string {
  const currencySymbol = currency === "USD" ? "$" : "";
  return `${currencySymbol}${OnboardingFeeAmountFormatter.format(amount)} ${currency} fixed fee`;
}

function formatEffectiveDatePolicy(policy: string): string {
  return policy === "entityActivationDate" ? "Your organization activation date" : policy;
}

function formatEndDatePolicy(policy: string): string {
  return policy === "none" ? "No fixed end date" : policy;
}
